import React, { useState } from 'react';
import PopupComponent from '../../components/PopupComponent';
import { MailCheck, Calendar, TrendingUp, CalendarDays } from 'lucide-react';
import styled from 'styled-components';

interface ReportSubscriptionPopupProps {
  isOpen: boolean;
  onClose: () => void;
  onSubscribe: (frequencies: string[]) => Promise<void>;
  loading?: boolean;
}

const CheckboxContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin: 24px 0;
`;

const CheckboxItem = styled.label`
  display: flex;
  align-items: center;
  padding: 16px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: white;

  &:hover {
    border-color: #3b82f6;
    background: #f0f9ff;
  }

  &.checked {
    border-color: #3b82f6;
    background: #eff6ff;
  }

  input[type="checkbox"] {
    width: 20px;
    height: 20px;
    cursor: pointer;
    margin-right: 12px;
    accent-color: #3b82f6;
  }

  .checkbox-content {
    flex: 1;
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .checkbox-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    background: #eff6ff;
    border-radius: 8px;
    color: #3b82f6;
  }

  &.checked .checkbox-icon {
    background: #3b82f6;
    color: white;
  }

  .checkbox-label {
    flex: 1;
  }

  .checkbox-title {
    font-size: 15px;
    font-weight: 600;
    color: #1f2937;
    margin: 0 0 4px 0;
  }

  .checkbox-description {
    font-size: 13px;
    color: #6b7280;
    margin: 0;
  }
`;

const PreviewSection = styled.div`
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 20px;
  margin: 24px 0;
  font-family: 'Courier New', monospace;
  font-size: 13px;
  line-height: 1.8;
  color: #374151;
  white-space: pre-line;
`;

const AlertBox = styled.div`
  background: #fef3c7;
  border-left: 4px solid #f59e0b;
  padding: 12px 16px;
  border-radius: 6px;
  margin: 16px 0;

  p {
    margin: 0;
    font-size: 14px;
    color: #92400e;
    font-weight: 500;
  }
`;

const FooterButtons = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;

const Button = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;

  ${({ $variant }) =>
    $variant === 'primary'
      ? `
    background: #06402b;
    color: white;
    &:hover {
      background: #052f1f;
    }
    &:disabled {
      background: #9ca3af;
      cursor: not-allowed;
    }
  `
      : `
    background: white;
    color: #6b7280;
    border: 1px solid #d1d5db;
    &:hover {
      background: #f9fafb;
    }
  `}
`;

const emailPreview = `📊 Daily Performance Summary
──────────────────
New Leads:  15 leads added
──────────────────
Calls Activity:
• Total Calls: 45 calls
• Connected: 32 calls
• Avg Call Time: 3.5 mins
──────────────────
Sales Updates:
• Total Sales: ₹75,000
• Sales: 12 completed
──────────────────
Tasks:
• Due Today: 8 tasks
• Completed: 5 tasks
• Overdue: 2 tasks`;

const frequencyOptions = [
  {
    value: 'daily',
    label: 'Daily Reports',
    description: 'Receive reports every day at 9:00 AM',
    icon: Calendar,
  },
  {
    value: 'weekly',
    label: 'Weekly Reports',
    description: 'Receive reports every Monday at 9:00 AM',
    icon: CalendarDays,
  },
  {
    value: 'monthly',
    label: 'Monthly Reports',
    description: 'Receive reports on the 1st of each month at 9:00 AM',
    icon: TrendingUp,
  },
];

const ReportSubscriptionPopup: React.FC<ReportSubscriptionPopupProps> = ({
  isOpen,
  onClose,
  onSubscribe,
  loading = false,
}) => {
  const [selectedFrequencies, setSelectedFrequencies] = useState<string[]>(['daily']);

  const handleToggleFrequency = (frequency: string) => {
    setSelectedFrequencies((prev) => {
      if (prev.includes(frequency)) {
        return prev.filter((f) => f !== frequency);
      }
      return [...prev, frequency];
    });
  };

  const handleSubscribe = async () => {
    if (selectedFrequencies.length === 0) {
      return; // Don't allow subscription with no frequencies selected
    }
    await onSubscribe(selectedFrequencies);
  };

  const footer = (
    <FooterButtons>
      <Button $variant="secondary" onClick={onClose} disabled={loading}>
        Maybe Later
      </Button>
      <Button
        $variant="primary"
        onClick={handleSubscribe}
        disabled={loading || selectedFrequencies.length === 0}
      >
        <MailCheck size={18} />
        {loading ? 'Subscribing...' : 'Subscribe'}
      </Button>
    </FooterButtons>
  );

  return (
    <PopupComponent
      isOpen={isOpen}
      onClose={onClose}
      title="Subscribe to Business Reports"
      size="lg"
      footer={footer}
    >
      <div style={{ padding: '8px 0' }}>
        <p style={{ fontSize: '15px', color: '#6b7280', marginBottom: '16px' }}>
          Choose how often you'd like to receive your business performance reports via email:
        </p>

        <CheckboxContainer>
          {frequencyOptions.map((option) => {
            const Icon = option.icon;
            const isChecked = selectedFrequencies.includes(option.value);

            return (
              <CheckboxItem
                key={option.value}
                className={isChecked ? 'checked' : ''}
              >
                <input
                  type="checkbox"
                  checked={isChecked}
                  onChange={() => handleToggleFrequency(option.value)}
                  disabled={loading}
                />
                <div className="checkbox-content">
                  <div className="checkbox-icon">
                    <Icon size={20} />
                  </div>
                  <div className="checkbox-label">
                    <p className="checkbox-title">{option.label}</p>
                    <p className="checkbox-description">{option.description}</p>
                  </div>
                </div>
              </CheckboxItem>
            );
          })}
        </CheckboxContainer>

        <AlertBox>
          <p>
            ℹ️ If you've already subscribed, updating your preferences will replace your previous subscription.
          </p>
        </AlertBox>

        <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#1f2937', marginBottom: '12px' }}>
          📧 Email Preview
        </h4>
        <PreviewSection>{emailPreview}</PreviewSection>
      </div>
    </PopupComponent>
  );
};

export default ReportSubscriptionPopup;
